import { Injectable } from '@angular/core'
import { DialogService } from 'primeng/dynamicdialog'
import { ToastAlertComponent } from '../../03_shared-components/01_alerts/toast-alert/toast-alert.component'
import { ExecuteDeleteAPI, ExecuteGetAPI, ExecutePostAPI, ExecutePutAPI } from './execute_api-calls'

@Injectable()
export class BusinessRulesEngineAPIs {
  constructor (public dialogService: DialogService, public toastAlert: ToastAlertComponent) {}

  async GetAllRules () {
    const path = '/rules'
    const messages = {
      success: '---> API Call Successful: Get All Rules',
      error: 'Error retrieving rules.'
    }

    const response = await ExecuteGetAPI(path, this, messages)

    if (response && response.is_success) {
      return response.data.sort((a, b) => a.priority_order - b.priority_order)
    }

    return [{}]
  }

  async UpdatePriorityOrder (rules) {
    const path = '/rules'
    const messages = {
      success: 'Successfully updated priority order.',
      error: 'Error updating priority order.',
      showSuccessToast: true
    }

    const data = {
      rules: rules.map(rule => {
        return {
          rule_id: rule.rule_id,
          priority_order: rule.priority_order
        }
      })
    }

    return await ExecutePutAPI(path, this, data, messages)
  }

  async GetAllConditionsAndActions () {
    const path = '/rules/get_conditions_actions'
    const messages = {
      success: '---> API Call Successful: Get All Conditions and Actions',
      error: 'Error retrieving conditions and actions.'
    }

    const response = await ExecuteGetAPI(path, this, messages)

    if (response && response.is_success) {
      return response.data
    }
  }

  async CreateRule (rule) {
    const path = '/rules'
    const messages = {
      success: 'Successfully added rule.',
      error: 'Error adding rule.',
      showSuccessToast: true
    }

    const data = {
      rule: {
        rule_name: rule.name,
        rule_description: '',
        priority_order: rule.priority_order,
        conditions: rule.conditions.map(condition => {
          return {
            condition_id: condition.condition_id,
            input_name: condition.input_name,
            input_value: condition.input_value
          }
        }),
        actions: rule.actions.map(action => {
          return {
            action_id: action.action_id,
            action_execution_order: action.action_execution_order,
            action_inputs: action.action_inputs.map(input => {
              return {
                input_name: input.input_name,
                input_value: input.input_value
              }
            })
          }
        })
      }
    }

    return await ExecutePostAPI(path, this, data, messages)
  }

  async UpdateRule (rule) {
    const path = `/rules/${rule.rule_id}`
    const messages = {
      success: 'Successfully updated rule.',
      error: 'Error updating rule.',
      showSuccessToast: true
    }

    const data = {
      rule: {
        rule_name: rule.name,
        rule_description: rule.description || '',
        priority_order: rule.priority_order,
        is_active: rule.is_active,
        conditions: rule.conditions.map(condition => {
          return {
            condition_id: condition.condition_id,
            input_name: condition.input_name,
            input_value: condition.input_value
          }
        }),
        actions: rule.actions.map(action => {
          return {
            action_id: action.action_id,
            action_execution_order: action.action_execution_order,
            action_inputs: action.action_inputs.map(input => {
              return {
                input_name: input.input_name,
                input_value: input.input_value
              }
            })
          }
        })
      }
    }

    return await ExecutePutAPI(path, this, data, messages)
  }

  async DeleteRule (rule_id: number) {
    const path = `/rules/${rule_id}`
    const messages = {
      success: 'Successfully deleted rule.',
      error: 'Error deleting rule.',
      showSuccessToast: true
    }

    return await ExecuteDeleteAPI(path, this, messages)
  }

  async DeleteRules (ruleIdsToDelete: number[]) {
    const path = '/rules/delete'
    const messages = {
      success: `Successfully deleted ${ruleIdsToDelete.length} rule(s).`,
      error: 'Error deleting rules.',
      showSuccessToast: true
    }

    const data = { rule: ruleIdsToDelete }

    return await ExecutePostAPI(path, this, data, messages)
  }

  async EnableRules (ruleIdsToEnable: number[]) {
    const path = '/rules'
    const messages = {
      success: `Successfully enabled ${ruleIdsToEnable.length} rule(s).`,
      error: 'Error enabling rules.',
      showSuccessToast: true
    }

    const data = {
      rules: ruleIdsToEnable.map(ruleId => {
        return {
          rule_id: ruleId,
          is_active: true
        }
      })
    }

    return await ExecutePutAPI(path, this, data, messages)
  }

  async DisableRules (ruleIdsToDisable: number[]) {
    const path = '/rules'
    const messages = {
      success: `Successfully disabled ${ruleIdsToDisable.length} rule(s).`,
      error: 'Error disabling rules.',
      showSuccessToast: true
    }

    const data = {
      rules: ruleIdsToDisable.map(ruleId => {
        return {
          rule_id: ruleId,
          is_active: false
        }
      })
    }

    return await ExecutePutAPI(path, this, data, messages)
  }
}
